import { Helmet } from "react-helmet-async";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import reviews from "../backend/db/reviews.json";
import styles from "./CustomerReviews.module.css";

// Average rating across all reviews (1 decimal), reused by the JSON-LD and the visible summary
const averageRating = reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : null;

// Structured data for reviews (JSON-LD)
const reviewSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "ווטרלס - Waterless",
  image: "https://waterless.co.il/assets/images/waterlessLogo.png",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: averageRating,
    bestRating: "5",
    worstRating: "1",
    ratingCount: reviews.length.toString(),
    reviewCount: reviews.length.toString(),
  },
  review: reviews.map((rec) => ({
    "@type": "Review",
    author: {
      "@type": "Person",
      name: rec.name,
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: rec.rating.toString(),
      bestRating: "5",
      worstRating: "1",
    },
    reviewBody: rec.text,
  })),
};

const ReviewCard = ({ name, job, text, rating, date }) => {
  // Generate initials from name for a more authentic avatar
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("");

  return (
    <div className={styles.reviewCard} role="article" aria-label={`חוות דעת מאת ${name}`}>
      <div className={styles.avatarInitials} aria-hidden="true">
        {initials}
      </div>
      <h3 className={styles.clientName}>{name}</h3>
      <h4 className={styles.clientJob}>{job}</h4>
      <div className={styles.reviewStars} aria-label={`דירוג ${rating} מתוך 5 כוכבים`}>
        <span className={styles.filledStars}>{"★".repeat(rating)}</span>
        <span className={styles.emptyStars}>{"☆".repeat(5 - rating)}</span>
      </div>
      <p className={styles.reviewText}>{text}</p>
      {date && <span className={styles.reviewDate}>{date}</span>}
    </div>
  );
};

const CustomerReviews = () => {
  if (!reviews.length) return null;
  return (
    <div className={styles.reviewsSection} aria-label="חוות דעת לקוחות">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>
      <div className="container-fluid px-3 px-md-5">
        <h2 className={styles.sectionTitle}>מה הלקוחות שלנו אומרים עלינו</h2>

        <div className={styles.ratingsSummary}>
          <div className={styles.averageRating}>
            <div className={styles.stars}>★★★★★</div>
            <span>{averageRating}/5</span>
          </div>
          <div className={styles.reviewCount}>מבוסס על {reviews.length} חוות דעת</div>
        </div>

        <div className={styles.carouselWrapper}>
          <Swiper
            modules={[A11y, Navigation, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true, dynamicBullets: true }}
            loop={true}
            a11y={{ prevSlideMessage: "חוות הדעת הקודמת", nextSlideMessage: "חוות הדעת הבאה", paginationBulletMessage: "מעבר לחוות דעת {{index}}" }}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
            }}
            className={styles.reviewsSwiper}
          >
            {reviews.map((rec) => (
              <SwiperSlide key={rec.id}>
                <ReviewCard
                  name={rec.name}
                  job={rec.job}
                  text={rec.text}
                  rating={rec.rating}
                  date={rec.date}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default CustomerReviews;
